// src/components/divesitedashboard/DivesitesTable.jsx
import React, { useState } from "react";
import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Button,
  Modal,
  Box,
  Typography,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import PoolIcon from "@mui/icons-material/Pool";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "@mui/material/styles";
import { LogDiveForm } from "../dives/LogDiveForm";
import DivesLogged from "./DivesLoggedChip";

// Enhanced glassmorphic table container
const GlassTableContainer = styled(TableContainer)(({ theme }) => ({
  background: `linear-gradient(135deg, 
    ${alpha(theme.palette.background.paper, 0.9)} 0%, 
    ${alpha(theme.palette.background.paper, 0.8)} 100%)`,
  backdropFilter: "blur(20px) saturate(1.1)",
  WebkitBackdropFilter: "blur(20px) saturate(1.1)",
  border: `1px solid ${alpha(theme.palette.common.white, 0.15)}`,
  borderRadius: 20,
  overflow: "hidden",
  boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.08)}`,
  position: "relative",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: `linear-gradient(90deg, 
      transparent, 
      ${alpha(theme.palette.primary.main, 0.3)}, 
      transparent)`,
    zIndex: 1,
  },

  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `radial-gradient(circle at 20% 30%, 
      ${alpha(theme.palette.primary.main, 0.02)} 0%, 
      transparent 50%), 
      radial-gradient(circle at 80% 70%, 
      ${alpha(theme.palette.secondary.main, 0.02)} 0%, 
      transparent 50%)`,
    pointerEvents: "none",
    zIndex: 0,
  },

  [theme.breakpoints.down("md")]: {
    borderRadius: 16,
  },
}));

// Enhanced glassmorphic table
const GlassTable = styled(Table)(({ theme }) => ({
  position: "relative",
  zIndex: 2,
  background: "transparent",
}));

// Enhanced glassmorphic table head
const GlassTableHead = styled(TableHead)(({ theme }) => ({
  background: `linear-gradient(135deg, 
    ${alpha(theme.palette.primary.main, 0.1)} 0%, 
    ${alpha(theme.palette.primary.dark, 0.05)} 100%)`,
  backdropFilter: "blur(10px)",
  WebkitBackdropFilter: "blur(10px)",
  position: "relative",

  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: `linear-gradient(90deg, 
      transparent, 
      ${alpha(theme.palette.primary.main, 0.2)}, 
      transparent)`,
  },
}));

// Enhanced glassmorphic table header cell
const GlassTableHeaderCell = styled(TableCell)(({ theme }) => ({
  background: "transparent",
  border: "none",
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  color: theme.palette.primary.main,
  fontWeight: 700,
  fontSize: "0.875rem",
  letterSpacing: "0.5px",
  textTransform: "uppercase",
  padding: theme.spacing(2, 3),
  position: "relative",

  "&:first-of-type": {
    paddingLeft: theme.spacing(3),
  },

  "&:last-of-type": {
    paddingRight: theme.spacing(3),
  },

  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(1.5, 2),
    fontSize: "0.75rem",

    "&:first-of-type": {
      paddingLeft: theme.spacing(2),
    },

    "&:last-of-type": {
      paddingRight: theme.spacing(2),
    },
  },
}));

// Enhanced glassmorphic table row
const GlassTableRow = styled(TableRow)(({ theme }) => ({
  background: "transparent",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  cursor: "pointer",
  position: "relative",

  "&:hover": {
    background: `linear-gradient(135deg, 
      ${alpha(theme.palette.primary.main, 0.04)} 0%, 
      ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
    transform: "translateX(4px)",

    "&::before": {
      opacity: 1,
      transform: "scaleY(1)",
    },
  },

  "&::before": {
    content: '""',
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: "3px",
    background: `linear-gradient(180deg, 
      ${theme.palette.primary.main}, 
      ${theme.palette.secondary.main})`,
    opacity: 0,
    transform: "scaleY(0.5)",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    borderRadius: "0 2px 2px 0",
  },

  "&:last-child td": {
    borderBottom: "none",
  },

  [theme.breakpoints.down("md")]: {
    "&:hover": {
      transform: "translateX(2px)",
    },
  },
}));

// Enhanced glassmorphic table cell
const GlassTableCell = styled(TableCell)(({ theme }) => ({
  background: "transparent",
  border: "none",
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.05)}`,
  padding: theme.spacing(2, 3),
  color: theme.palette.text.primary,
  fontSize: "0.875rem",
  position: "relative",

  "&:first-of-type": {
    paddingLeft: theme.spacing(3),
  },

  "&:last-of-type": {
    paddingRight: theme.spacing(3),
  },

  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(1.5, 2),
    fontSize: "0.8rem",

    "&:first-of-type": {
      paddingLeft: theme.spacing(2),
    },

    "&:last-of-type": {
      paddingRight: theme.spacing(2),
    },
  },
}));

// Enhanced glassmorphic delete button for table
const GlassTableDeleteButton = styled(IconButton)(({ theme }) => ({
  background: `linear-gradient(135deg, 
    ${alpha(theme.palette.background.paper, 0.8)} 0%, 
    ${alpha(theme.palette.background.paper, 0.6)} 100%)`,
  backdropFilter: "blur(10px)",
  WebkitBackdropFilter: "blur(10px)",
  border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
  borderRadius: 8,
  width: 32,
  height: 32,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",

  "&:hover": {
    background: `linear-gradient(135deg, 
      ${alpha(theme.palette.error.main, 0.9)} 0%, 
      ${alpha(theme.palette.error.dark, 0.8)} 100%)`,
    borderColor: alpha(theme.palette.error.main, 0.4),
    transform: "scale(1.05)",
    color: theme.palette.error.contrastText,
    boxShadow: `0 4px 16px ${alpha(theme.palette.error.main, 0.3)}`,
  },

  "&:active": {
    transform: "scale(0.95)",
  },

  [theme.breakpoints.down("md")]: {
    width: 28,
    height: 28,
    borderRadius: 6,
  },
}));

// Enhanced glassmorphic log dive button for table
const GlassTableLogButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(135deg, 
    ${theme.palette.primary.main} 0%, 
    ${theme.palette.primary.dark} 100%)`,
  backdropFilter: "blur(10px)",
  WebkitBackdropFilter: "blur(10px)",
  border: `1px solid ${alpha(theme.palette.primary.light, 0.3)}`,
  borderRadius: 8,
  textTransform: "none",
  fontWeight: 600,
  position: "relative",
  overflow: "hidden",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.2)}`,
  minWidth: "auto",
  padding: theme.spacing(0.5, 1.5),
  fontSize: "0.75rem",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: `linear-gradient(90deg, 
      transparent, 
      ${alpha(theme.palette.common.white, 0.4)}, 
      transparent)`,
    borderRadius: "8px 8px 0 0",
  },

  "&:hover": {
    background: `linear-gradient(135deg, 
      ${theme.palette.primary.light} 0%, 
      ${theme.palette.primary.main} 100%)`,
    transform: "translateY(-1px)",
    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
    borderColor: alpha(theme.palette.primary.light, 0.5),
  },

  "&:active": {
    transform: "translateY(0) scale(0.98)",
  },

  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(0.4, 1.2),
    fontSize: "0.7rem",
  },
}));

// Reuse the modal components from the grid
const GlassModal = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, 
    ${alpha(theme.palette.background.paper, 0.95)} 0%, 
    ${alpha(theme.palette.background.paper, 0.9)} 100%)`,
  backdropFilter: "blur(30px) saturate(1.2)",
  WebkitBackdropFilter: "blur(30px) saturate(1.2)",
  border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
  borderRadius: 24,
  overflow: "hidden",
  boxShadow: `0 24px 64px ${alpha(theme.palette.common.black, 0.2)}`,
  position: "relative",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "2px",
    background: `linear-gradient(90deg, 
      transparent, 
      ${alpha(theme.palette.primary.main, 0.6)}, 
      ${alpha(theme.palette.secondary.main, 0.4)}, 
      transparent)`,
    zIndex: 1,
  },

  [theme.breakpoints.down("md")]: {
    borderRadius: 20,
  },
}));

const GlassModalHeader = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, 
    ${alpha(theme.palette.primary.main, 0.9)} 0%, 
    ${alpha(theme.palette.primary.dark, 0.8)} 100%)`,
  backdropFilter: "blur(15px)",
  WebkitBackdropFilter: "blur(15px)",
  border: "none",
  borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
  position: "relative",
  zIndex: 2,

  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: `linear-gradient(90deg, 
      transparent, 
      ${alpha(theme.palette.common.white, 0.3)}, 
      transparent)`,
  },
}));

const DivesitesTable = ({
  divesites,
  onDivesiteClick,
  onRemoveDivesite,
  isDeleting,
}) => {
  const { currentUser, userObjectId } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const [openModal, setOpenModal] = useState(null);

  const handleOpenModal = (event, divesiteId) => {
    event.stopPropagation();
    setOpenModal(divesiteId);
  };

  const handleCloseModal = () => {
    setOpenModal(null);
  };

  return (
    <>
      <GlassTableContainer>
        <GlassTable aria-label="divesites table">
          <GlassTableHead>
            <TableRow>
              <GlassTableHeaderCell>Site Name</GlassTableHeaderCell>
              <GlassTableHeaderCell>Location</GlassTableHeaderCell>
              {!isMobile && (
                <GlassTableHeaderCell>Description</GlassTableHeaderCell>
              )}
              <GlassTableHeaderCell>Dives</GlassTableHeaderCell>
              <GlassTableHeaderCell align="right">Actions</GlassTableHeaderCell>
            </TableRow>
          </GlassTableHead>
          <TableBody>
            {divesites.map((divesite) => {
              const {
                _id,
                uddf: {
                  divesite: {
                    site: {
                      name: siteName,
                      geography: { province_short, province, city },
                    },
                  },
                },
                metadata: { oneliner },
              } = divesite;

              return (
                <GlassTableRow
                  key={_id}
                  onClick={() => onDivesiteClick(divesite)}
                >
                  <GlassTableCell component="th" scope="row">
                    <Box>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        color="primary.main"
                        sx={{
                          mb: 0.5,
                          fontSize: isMobile ? "0.8rem" : "0.875rem",
                          lineHeight: 1.2,
                        }}
                      >
                        {siteName}
                      </Typography>
                      {isMobile && oneliner && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: "-webkit-box",
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            fontSize: "0.7rem",
                          }}
                        >
                          {oneliner}
                        </Typography>
                      )}
                    </Box>
                  </GlassTableCell>

                  <GlassTableCell>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <LocationOnIcon
                        fontSize="small"
                        sx={{
                          color: "text.secondary",
                          fontSize: isMobile ? 14 : 16,
                        }}
                      />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          fontSize: isMobile ? "0.75rem" : "0.875rem",
                          lineHeight: 1.3,
                        }}
                      >
                        {divesite.uddf?.divesite?.site?.geography?.address
                          ?.city || city}
                        {(divesite.uddf?.divesite?.site?.geography?.address
                          ?.province ||
                          province_short ||
                          province ||
                          divesite.uddf?.divesite?.site?.geography?.address
                            ?.country) &&
                          `, ${
                            divesite.uddf?.divesite?.site?.geography?.address
                              ?.province ||
                            province_short ||
                            province ||
                            divesite.uddf?.divesite?.site?.geography?.address
                              ?.country
                          }`}
                      </Typography>
                    </Box>
                  </GlassTableCell>

                  {!isMobile && (
                    <GlassTableCell>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          fontSize: "0.8rem",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          lineHeight: 1.3,
                        }}
                      >
                        {oneliner || "—"}
                      </Typography>
                    </GlassTableCell>
                  )}

                  <GlassTableCell>
                    <DivesLogged divesiteId={_id} />
                  </GlassTableCell>

                  <GlassTableCell align="right">
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        alignItems: "center",
                        justifyContent: "flex-end",
                      }}
                    >
                      {currentUser && userObjectId && (
                        <GlassTableLogButton
                          onClick={(e) => handleOpenModal(e, _id)}
                          variant="contained"
                          size="small"
                          startIcon={
                            <PoolIcon sx={{ fontSize: isMobile ? 14 : 16 }} />
                          }
                        >
                          {isMobile ? "Log" : "Log Dive"}
                        </GlassTableLogButton>
                      )}

                      <Tooltip title="Remove from dashboard">
                        <GlassTableDeleteButton
                          aria-label="delete divesite"
                          onClick={(e) => {
                            e.stopPropagation();
                            onRemoveDivesite(_id);
                          }}
                          disabled={isDeleting}
                        >
                          <DeleteIcon
                            fontSize={isMobile ? "small" : "medium"}
                          />
                        </GlassTableDeleteButton>
                      </Tooltip>
                    </Box>
                  </GlassTableCell>
                </GlassTableRow>
              );
            })}
          </TableBody>
        </GlassTable>
      </GlassTableContainer>

      {/* Log Dive Modals */}
      {divesites.map((divesite) => (
        <Modal
          key={`modal-${divesite._id}`}
          open={openModal === divesite._id}
          onClose={handleCloseModal}
          aria-labelledby={`log-dive-modal-title-${divesite._id}`}
          aria-describedby={`log-dive-modal-description-${divesite._id}`}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: isMobile ? 1 : 2,
          }}
        >
          <GlassModal
            sx={{
              width: { xs: "95vw", sm: "90%", md: 600 },
              maxWidth: { xs: "95vw", sm: "90vw", md: 600 },
              maxHeight: { xs: "95vh", sm: "90vh" },
              display: "flex",
              flexDirection: "column",
            }}
          >
            <GlassModalHeader
              sx={{
                p: isMobile ? 2 : 3,
                color: theme.palette.primary.contrastText,
              }}
            >
              <Typography
                id={`log-dive-modal-title-${divesite._id}`}
                variant={isMobile ? "h6" : "h5"}
                component="h2"
                fontWeight="bold"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  fontSize: isMobile ? "1.1rem" : "1.5rem",
                }}
              >
                <PoolIcon sx={{ fontSize: isMobile ? 20 : 24 }} />
                Log Your Dive
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  opacity: 0.9,
                  mt: 0.5,
                  fontSize: isMobile ? "0.8rem" : "0.875rem",
                }}
              >
                {divesite.uddf?.divesite?.site?.name}
              </Typography>
            </GlassModalHeader>

            <Box
              sx={{
                p: isMobile ? 2 : 3,
                overflow: "auto",
                flexGrow: 1,
              }}
            >
              <LogDiveForm
                divesiteid={divesite._id}
                onClose={handleCloseModal}
              />
            </Box>

            <Box
              sx={{
                p: isMobile ? 1.5 : 2,
                borderTop: `1px solid ${alpha(
                  theme.palette.common.white,
                  0.1
                )}`,
                background: `linear-gradient(135deg, 
                  ${alpha(theme.palette.background.paper, 0.6)} 0%, 
                  ${alpha(theme.palette.background.paper, 0.4)} 100%)`,
                backdropFilter: "blur(15px)",
                display: "flex",
                justifyContent: isMobile ? "center" : "flex-end",
              }}
            >
              <Button
                onClick={handleCloseModal}
                variant={isMobile ? "contained" : "outlined"}
                sx={{
                  textTransform: "none",
                  width: isMobile ? "100%" : "auto",
                  maxWidth: isMobile ? 200 : "auto",
                  borderRadius: 2,
                }}
              >
                Close
              </Button>
            </Box>
          </GlassModal>
        </Modal>
      ))}
    </>
  );
};

export default DivesitesTable;
